import { Maybe } from "@/gql/graphql";

import { WeightChartData } from "../types/WeightChartData";

/**
 * This function creates the data for the line graph and computes a value for the null weight values
 * in order to have a straight line since Victory Charts does not allow connecting lines if there's a
 * null value in between
 * @param data WeightChartData
 * @param previousWeight WeightLog
 * @returns
 */
export const getLineData = (
  data: WeightChartData[],
  previousWeight?: Maybe<number>
): WeightChartData[] => {
  // Get the the index of the first data with weight value
  const firstIndex = data.findIndex((log) => (log.weight ?? 0) > 0);

  // Reverse the data in order to get the last index of the data with weight value
  const reversedData = [...data].reverse();

  // Since the original date Data has index 6 as the last index
  const DATA_LAST_INDEX = 6;
  // In order to get the correct index, subtract the index of the reversed data from DATA_LAST_INDEX
  const lastIndex =
    DATA_LAST_INDEX - reversedData.findIndex((log) => (log.weight ?? 0) > 0);

  // Checks for undefined values. If undefined is encountered, it replaces it with the previous defined value.
  const interpolateData = (data: string | any[]) => {
    const interpolatedData = [];
    let previousValue = null;

    for (let i = 0; i < data.length; i++) {
      const item = data[i];

      if (item.weight !== undefined) {
        previousValue = item.weight;
        interpolatedData.push(item);
      } else if (previousValue !== null) {
        interpolatedData.push({ date: item.date, weight: previousValue });
      }
    }

    return interpolatedData;
  };

  // check if firstIndex is not undefined, if it is, there's no data for the week so just return the original data
  if (firstIndex >= 0) {
    // create a copy of the data to save the temp values of the null weight values
    const dataCopy = [...data];
    // this is the last index of data with weight values
    let lastIndexDataValue = null;

    const lineData = [];

    for (const [index, log] of data.entries()) {
      // Check if the first data is null, then change the weight to the previous weight value
      if (index === 0 && !log.weight) {
        const newLog = { ...log };

        newLog.weight = previousWeight ? previousWeight : 0;

        // check if the previousWeight is not null, then replace the dataCopy[0] with the new data
        // as a reference for the next null values
        if (previousWeight) {
          lastIndexDataValue = index;
          dataCopy[0] = {
            date: log.date,
            weight: previousWeight,
          };
        }

        lineData.push(newLog);
      } else if (
        index === 6 ||
        log.weight ||
        index > lastIndex ||
        (!previousWeight && index < firstIndex)
      ) {
        lastIndexDataValue = index;
        lineData.push(log);
      } else {
        // save the index as the new last index with value
        lastIndexDataValue = index;

        lineData.push({
          date: log.date,
          weight: undefined,
        });
      }
    }
    return interpolateData(lineData);
  } else {
    return data;
  }
};
